'use client';

import { useState, useEffect } from 'react';
import { consentConfig, syncConsentToZaraz } from '@/lib/consent-config';
import { useTheme } from './ThemeProvider';
import { isNative } from '@/lib/platform';

interface ConsentState {
  accepted: string[];
  rejected: string[];
  version: number;
  timestamp: number;
}

export default function ConsentManager() {
  const { theme } = useTheme();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [categoryToggles, setCategoryToggles] = useState<Record<string, boolean>>({});

  // Load consent from localStorage on mount

  useEffect(() => {
    // Skip consent UI on native — no cookies, no Zaraz, no Cloudflare edge.
    // Native privacy is handled by App Store / Play Store privacy disclosures.
    if (isNative()) return;

    console.log('[Consent] Initializing ConsentManager...');

    const stored = localStorage.getItem(consentConfig.storageKey);
    console.log('[Consent] Stored consent:', stored);

    if (stored) {
      try {
        const parsed: ConsentState = JSON.parse(stored);
        setConsent(parsed);

        // Sync to Zaraz
        syncConsentToZaraz(parsed.accepted);

        // Check if expired (1 year)
        const isExpired = Date.now() - parsed.timestamp > 365 * 24 * 60 * 60 * 1000;
        console.log('[Consent] Expired:', isExpired);

        if (isExpired) {
          setShowBanner(true);
        }
      } catch (e) {
        console.error('[Consent] Error parsing stored consent:', e);
        setShowBanner(true);
      }
    } else {
      // No consent yet - show banner after short delay to reduce INP
      console.log('[Consent] No stored consent, will show banner...');

      // Fallback for browsers without requestIdleCallback
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => {
          console.log('[Consent] Showing banner (via requestIdleCallback)');
          setShowBanner(true);
        }, { timeout: 2000 });
      } else {
        setTimeout(() => {
          console.log('[Consent] Showing banner (via setTimeout fallback)');
          setShowBanner(true);
        }, 2000);
      }
    }
  }, []);
   

  // Initialize category toggles when showing details
   
  useEffect(() => {
    if (showDetails && consent) {
      const toggles: Record<string, boolean> = {};
      consentConfig.categories.forEach(cat => {
        if (cat.required) {
          toggles[cat.id] = true;
        } else {
          toggles[cat.id] = consent.accepted.includes(cat.id);
        }
      });
      setCategoryToggles(toggles);
    }
  }, [showDetails, consent]);
   

  // Save consent to localStorage and API
  const saveConsent = async (accepted: string[], rejected: string[]) => {
    const newConsent: ConsentState = {
      accepted,
      rejected,
      version: consentConfig.version,
      timestamp: Date.now(),
    };

    // Save to localStorage
    localStorage.setItem(consentConfig.storageKey, JSON.stringify(newConsent));
    setConsent(newConsent);

    // Sync to Zaraz
    syncConsentToZaraz(accepted);

    // Save to backend API
    try {
      const response = await fetch(consentConfig.api.save, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consentVersion: consentConfig.version,
          acceptedCategories: accepted,
          rejectedCategories: rejected,
          language: navigator.language,
        }),
      });

      if (!response.ok) {
        console.error('[Consent] Failed to save to backend:', response.statusText);
      }
    } catch (error) {
      console.error('[Consent] Error saving to backend:', error);
      // Non-blocking - localStorage is primary storage
    }

    setShowBanner(false);
    setShowDetails(false);
  };

  const handleAcceptAll = () => {
    const accepted = consentConfig.categories
      .filter(cat => !cat.required)
      .map(cat => cat.id);
    const rejected: string[] = [];

    accepted.unshift('necessary'); // Always include necessary
    saveConsent(accepted, rejected);
  };

  const handleRejectAll = () => {
    const accepted = ['necessary']; // Only necessary
    const rejected = consentConfig.categories
      .filter(cat => !cat.required)
      .map(cat => cat.id);

    saveConsent(accepted, rejected);
  };

  const handleSavePreferences = () => {
    const accepted = Object.entries(categoryToggles)
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);
    const rejected = Object.entries(categoryToggles)
      .filter(([_, enabled]) => !enabled)
      .map(([id]) => id);

    saveConsent(accepted, rejected);
  };

  const bgColor = theme === 'dark' ? '#0A1E2E' : '#FFFFFF';
  const textColor = theme === 'dark' ? '#E8D5B7' : '#1F2937';
  const borderColor = theme === 'dark' ? 'rgba(232, 152, 65, 0.2)' : '#E5E7EB';

  // Show floating widget when consent has already been given (banner is hidden)
  if (!showBanner && consent) {
    return (
      <button
        onClick={() => {
          // Initialize toggles from current consent before showing
          const toggles: Record<string, boolean> = {};
          consentConfig.categories.forEach(cat => {
            if (cat.required) {
              toggles[cat.id] = true;
            } else {
              toggles[cat.id] = consent.accepted.includes(cat.id);
            }
          });
          setCategoryToggles(toggles);
          setShowDetails(true);
          setShowBanner(true);
        }}
        aria-label="Cookie preferences"
        title="Manage cookie preferences"
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '16px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.15)' : 'rgba(232, 152, 65, 0.1)',
          border: `1px solid ${theme === 'dark' ? 'rgba(232, 152, 65, 0.3)' : 'rgba(232, 152, 65, 0.4)'}`,
          color: '#E89841',
          fontSize: '18px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9998,
          transition: 'all 0.2s',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(232, 152, 65, 0.25)' : 'rgba(232, 152, 65, 0.2)';
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(232, 152, 65, 0.15)' : 'rgba(232, 152, 65, 0.1)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        🍪
      </button>
    );
  }

  if (!showBanner) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: bgColor,
        borderTop: `1px solid ${borderColor}`,
        padding: '20px',
        zIndex: 9999,
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {!showDetails ? (
          // Banner view
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: textColor, marginBottom: '8px' }}>
                {consentConfig.banner.title}
              </h3>
              <p style={{ fontSize: '14px', color: theme === 'dark' ? 'rgba(232, 213, 183, 0.8)' : '#6B7280', lineHeight: '1.5' }}>
                {consentConfig.banner.description}{' '}
                <a
                  href={consentConfig.banner.privacyPolicyUrl}
                  style={{ color: '#E89841', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </a>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowDetails(true)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid rgba(232, 152, 65, 0.5)',
                  backgroundColor: 'transparent',
                  color: '#E89841',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(232, 152, 65, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {consentConfig.banner.manageLabel}
              </button>

              <button
                onClick={handleRejectAll}
                style={{
                  padding: '10px 20px',
                  border: '1px solid rgba(232, 152, 65, 0.5)',
                  backgroundColor: 'transparent',
                  color: '#E89841',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(232, 152, 65, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {consentConfig.banner.rejectAllLabel}
              </button>

              <button
                onClick={handleAcceptAll}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#E89841',
                  color: '#0A1E2E',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(232, 152, 65, 0.9)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#E89841'}
              >
                {consentConfig.banner.acceptAllLabel}
              </button>
            </div>
          </div>
        ) : (
          // Details view
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: textColor }}>
                Manage Cookie Preferences
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: theme === 'dark' ? 'rgba(232, 213, 183, 0.6)' : '#9CA3AF',
                  cursor: 'pointer',
                  padding: '0',
                  lineHeight: '1',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {consentConfig.categories.map(category => (
                <div
                  key={category.id}
                  style={{
                    padding: '16px',
                    backgroundColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.05)' : '#F9FAFB',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: textColor, marginBottom: '4px' }}>
                      {category.name}
                    </div>
                    <div style={{ fontSize: '14px', color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#6B7280' }}>
                      {category.description}
                    </div>
                  </div>

                  <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '24px' }}>
                    <input
                      type="checkbox"
                      checked={categoryToggles[category.id] || false}
                      disabled={category.required}
                      onChange={(e) => setCategoryToggles({ ...categoryToggles, [category.id]: e.target.checked })}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        cursor: category.required ? 'not-allowed' : 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: categoryToggles[category.id] ? '#E89841' : (theme === 'dark' ? 'rgba(232, 213, 183, 0.2)' : '#D1D5DB'),
                        transition: '0.2s',
                        borderRadius: '24px',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          content: '',
                          height: '18px',
                          width: '18px',
                          left: categoryToggles[category.id] ? '26px' : '3px',
                          bottom: '3px',
                          backgroundColor: 'white',
                          transition: '0.2s',
                          borderRadius: '50%',
                        }}
                      />
                    </span>
                  </label>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={handleSavePreferences}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#E89841',
                  color: '#0A1E2E',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                {consentConfig.banner.saveLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
